import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DBSentence, SentenceStatus, DBCollection, Table } from '../models/db_models';
import {
  getRandomSentences,
  updateCollectionCounts,
  getById,
  save
} from '../utils/dao';
import {
  parseIntoWordChunks,
  generateAnswerOptions,
  buildVocabularyPool,
  canBeMarkedAsDone,
  getKnownSentenceAlternatives,
  WordChunk
} from '../utils/quizUtils';
import { SPEECH_STORE } from '../stores/SpeechStore';

type QuizState = 'loading' | 'initial' | 'active' | 'complete';

function Quiz() {
  const { collectionId, sentenceId } = useParams<{ collectionId: string; sentenceId: string }>();
  const navigate = useNavigate();

  // Data
  const [sentence, setSentence] = useState<DBSentence | null>(null);
  const [collection, setCollection] = useState<DBCollection | null>(null);
  const [vocabularyPool, setVocabularyPool] = useState<string[]>([]);
  const [wordChunks, setWordChunks] = useState<WordChunk[]>([]);

  // Quiz state
  const [quizState, setQuizState] = useState<QuizState>('loading');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [answerOptions, setAnswerOptions] = useState<string[]>([]);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [incorrectAnswers, setIncorrectAnswers] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState<Set<number>>(new Set());
  const [knownSentenceIndex, setKnownSentenceIndex] = useState(0);
  const [knownAlternatives, setKnownAlternatives] = useState<string[]>([]);

  // Error/loading
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadQuiz = async () => {
      if (!collectionId || !sentenceId) {
        setError('Missing collection or sentence ID');
        return;
      }

      try {
        // Get collection metadata
        const collectionData = await getById(Table.COLLECTIONS, collectionId) as DBCollection | undefined;
        if (!collectionData) {
          setError('Collection not found');
          return;
        }

        // Get the specific sentence
        const sentenceData = await getById(Table.SENTENCES, sentenceId) as DBSentence | undefined;
        if (!sentenceData) {
          setError('Sentence not found');
          return;
        }

        // Get random sentences for vocabulary pool
        const randomSentences = await getRandomSentences(collectionId, 10);
        const vocab = buildVocabularyPool(randomSentences);

        // Parse target sentence into word chunks
        const chunks = parseIntoWordChunks(sentenceData.targetSentence);

        // Parse known sentence alternatives
        const alternatives = getKnownSentenceAlternatives(sentenceData.knownSentence);

        setCollection(collectionData);
        setSentence(sentenceData);
        setVocabularyPool(vocab);
        setWordChunks(chunks);
        setKnownAlternatives(alternatives);
        setQuizState('initial');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    };

    loadQuiz();
  }, [collectionId, sentenceId]);

  const startQuiz = () => {
    if (wordChunks.length === 0) return;

    // Generate answer options for first word
    const options = generateAnswerOptions(wordChunks[0].word, vocabularyPool);
    setAnswerOptions(options);
    setQuizState('active');
  };

  const handleAnswerClick = (answer: string, index: number) => {
    if (!sentence || currentWordIndex >= wordChunks.length) return;

    const correctWord = wordChunks[currentWordIndex].word;

    if (answer === correctWord) {
      // Correct answer
      setCorrectAnswers(prev => prev + 1);
      setWrongAnswers(new Set());

      const nextIndex = currentWordIndex + 1;
      setCurrentWordIndex(nextIndex);

      if (nextIndex < wordChunks.length) {
        // Generate options for next word
        const options = generateAnswerOptions(wordChunks[nextIndex].word, vocabularyPool);
        setAnswerOptions(options);
      } else {
        // Quiz complete - speak the full sentence
        setQuizState('complete');
        if (sentence) {
          SPEECH_STORE.speak(sentence.targetSentence, collection?.targetLanguage || 'en', {
            enabledVoices: collection?.enabledVoices,
            speed: collection?.speechSpeed || 0.8
          });
        }
      }
    } else {
      // Incorrect answer
      setIncorrectAnswers(prev => prev + 1);
      setWrongAnswers(prev => new Set(prev).add(index));
    }
  };

  const handleMarkAsDone = async () => {
    if (!sentence || !collectionId) return;

    const updatedSentence = { ...sentence, status: SentenceStatus.DONE, doneAt: Date.now() };
    await save(updatedSentence);
    await updateCollectionCounts(sentence.collectionId);

    // Navigate to quiz route to find next TODO sentence
    navigate(`/collection/${collectionId}/quiz`);
  };

  const handleRepeatLater = async () => {
    if (!sentence || !collectionId) return;

    const updatedSentence = { ...sentence, status: SentenceStatus.REPEAT };
    await save(updatedSentence);
    await updateCollectionCounts(sentence.collectionId);

    // Navigate to quiz route to find next TODO sentence
    navigate(`/collection/${collectionId}/quiz`);
  };

  const handleSkip = async () => {
    if (!sentence || !collectionId) return;

    const updatedSentence = { ...sentence, status: SentenceStatus.SKIPPED };
    await save(updatedSentence);
    await updateCollectionCounts(sentence.collectionId);

    // Navigate to quiz route to find next TODO sentence
    navigate(`/collection/${collectionId}/quiz`);
  };

  const cycleKnownSentence = () => {
    setKnownSentenceIndex((prev) => (prev + 1) % knownAlternatives.length);
  };

  const renderTargetSentence = () => {
    return wordChunks.map((chunk, index) => {
      if (index < currentWordIndex) {
        return <span key={index}>{chunk.chunk} </span>;
      } else if (index === currentWordIndex && quizState === 'complete') {
        return <span key={index}>{chunk.chunk} </span>;
      } else {
        return <span key={index}>___ </span>;
      }
    });
  };

  if (quizState === 'loading') {
    return (
      <div className="container text-center mt-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading quiz...</span>
        </div>
        <p className="mt-3">Loading quiz...</p>
      </div>
    );
  }

  if (error || !sentence) {
    return (
      <div className="container text-center mt-5">
        <p className="text-danger">{error || 'No sentence found'}</p>
        <p className="mt-4">
          <Link to={`/collection/${collectionId}`} className="text-primary">← Back to Collection</Link>
        </p>
      </div>
    );
  }

  const canMarkDone = canBeMarkedAsDone(incorrectAnswers, wordChunks.length);

  return (
    <div className="container py-4">
      <div className="w-100 mx-auto" style={{ maxWidth: '800px' }}>
        {/* Stats */}
        <div className="mb-3 small text-muted">
          <span className="text-success">Correct: {correctAnswers}</span>
          {' | '}
          <span className="text-danger">Incorrect: {incorrectAnswers}</span>
        </div>

        {/* Known Sentence */}
        <div
          className="p-3 mb-3 bg-dark rounded text-white"
          style={{
            opacity: quizState === 'initial' ? 1 : 0.5,
            cursor: knownAlternatives.length > 1 ? 'pointer' : 'default'
          }}
          onClick={knownAlternatives.length > 1 ? cycleKnownSentence : undefined}
        >
          <div className="fs-5 mb-1">
            {knownAlternatives[knownSentenceIndex]}
          </div>
          {knownAlternatives.length > 1 && (
            <small className="text-muted">
              Translation {knownSentenceIndex + 1}/{knownAlternatives.length} (click to cycle)
            </small>
          )}
        </div>

        {/* Target Sentence */}
        <div className="p-3 mb-4 bg-secondary rounded fs-5 text-white" style={{ minHeight: '60px' }}>
          {renderTargetSentence()}
        </div>

        {/* Quiz Buttons */}
        {quizState === 'initial' && (
          <button
            onClick={startQuiz}
            className="btn btn-info btn-lg fw-bold"
          >
            Start Quiz
          </button>
        )}

        {quizState === 'active' && currentWordIndex < wordChunks.length && (
          <div className="row g-3 mx-auto" style={{ maxWidth: '500px' }}>
            {answerOptions.map((option, index) => (
              <div key={index} className="col-6">
                <button
                  onClick={() => handleAnswerClick(option, index)}
                  className={`btn w-100 py-3 fs-5 text-uppercase ${
                    wrongAnswers.has(index) ? 'btn-danger' : 'btn-dark'
                  }`}
                >
                  {option}
                </button>
              </div>
            ))}
          </div>
        )}

        {quizState === 'complete' && (
          <div>
            <div className="mb-4 fs-4">
              {canMarkDone ? (
                <span className="text-success">✓ Correct!</span>
              ) : (
                <span className="text-danger">Too many errors</span>
              )}
            </div>

            <div className="d-flex flex-column gap-2 mx-auto" style={{ maxWidth: '400px' }}>
              <button
                onClick={() => sentence && SPEECH_STORE.speak(sentence.targetSentence, collection?.targetLanguage || 'en', {
                  enabledVoices: collection?.enabledVoices,
                  speed: collection?.speechSpeed || 0.8
                })}
                className="btn btn-info fw-bold"
              >
                🔊 Read Sentence
              </button>

              {canMarkDone && (
                <button
                  onClick={handleMarkAsDone}
                  className="btn btn-success btn-lg fw-bold"
                >
                  Mark as Done & Continue
                </button>
              )}

              <button
                onClick={handleSkip}
                className="btn btn-warning fw-bold"
              >
                Skip & Continue
              </button>

              <button
                onClick={handleRepeatLater}
                className="btn btn-danger"
              >
                Repeat Later
              </button>

              <Link
                to={`/collection/${collectionId}`}
                className="btn btn-secondary"
              >
                Back to Collection
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Quiz;
